import { ApolloError, useMutation, useQuery } from "@apollo/client";
import { Autocomplete, Stack, TextField, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { EpisodicAttachableContainersQuery } from "generated-graphql/graphql";
import { useState } from "react";

import { CONTAINER_STATUS_LABELS } from "../Containers/containerDisplay";
import {
  EPISODIC_ATTACHABLE_CONTAINERS_QUERY,
  SET_CONTAINERS_EPISODIC_EVENT_MUTATION,
} from "./api";
import { episodicReliefAdvisory } from "./episodicDisplay";

type ContainerOption = NonNullable<
  EpisodicAttachableContainersQuery["episodicEvent"]
>["attachableContainers"][number];

function containerOptionLabel(option: ContainerOption): string {
  const area = option.accumulationArea?.name ?? "No area";
  return `${option.containerLabel} · ${CONTAINER_STATUS_LABELS[option.status]} · ${area}`;
}

/** BAD_USER_INPUT errors surface their server message verbatim; anything else
 * falls back to a generic toast. */
export function describeAttachError(error: unknown, fallback: string): string {
  if (!(error instanceof ApolloError)) {
    return fallback;
  }
  const badUserInput = error.graphQLErrors.find((e) => e.extensions?.code === "BAD_USER_INPUT");
  return badUserInput?.message ?? fallback;
}

export type AttachContainerDialogProps = {
  eventId: string;
  open: boolean;
  onClose: () => void;
};

/**
 * A multi-select over the event's `attachableContainers` — the server decides
 * what is offered, mirroring its own attach guard — then a single mutation
 * call for the whole selection. Server-side rejections are shown verbatim and
 * leave the dialog open, with the picks intact: the attach is one transaction,
 * so a refusal means nothing was attached and the reader edits the selection
 * rather than starting it again.
 */
export function AttachContainerDialog({ eventId, open, onClose }: AttachContainerDialogProps) {
  const alerts = useAlerts();
  const [selectedContainers, setSelectedContainers] = useState<ContainerOption[]>([]);

  const {
    data,
    loading: loadingOptions,
    error: optionsError,
  } = useQuery(EPISODIC_ATTACHABLE_CONTAINERS_QUERY, {
    variables: { eventId },
    skip: !open,
    fetchPolicy: "cache-and-network",
  });
  const containerOptions: ContainerOption[] = data?.episodicEvent?.attachableContainers ?? [];
  // Only when the server offered nothing; a filter miss keeps MUI's default.
  const noneEligible = !loadingOptions && !optionsError && containerOptions.length === 0;
  const reliefAdvisory = data?.episodicEvent
    ? episodicReliefAdvisory(data.episodicEvent.relief)
    : null;

  const [setContainersEpisodicEvent, { loading: attaching }] = useMutation(
    SET_CONTAINERS_EPISODIC_EVENT_MUTATION,
    { refetchQueries: ["FacilityEpisodicEvents", "FacilityAccumulationSummary"] }
  );

  const handleClose = () => {
    setSelectedContainers([]);
    onClose();
  };

  const handleAttach = async () => {
    if (selectedContainers.length === 0) {
      return;
    }
    const count = selectedContainers.length;
    await setContainersEpisodicEvent({
      variables: {
        containerIds: selectedContainers.map((container) => container.id),
        episodicEventId: eventId,
      },
      onCompleted: () => {
        alerts.success(
          count === 1 ? "Container attached to event" : `${count} containers attached to event`
        );
        handleClose();
      },
      onError: (error) => {
        // The whole selection is one transaction, so a refusal attached none
        // of them — the dialog keeps the picks so the offender can be removed
        // rather than the selection rebuilt.
        alerts.error(
          describeAttachError(error, "An error occurred while attaching the containers")
        );
      },
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      title="Attach existing containers"
      primaryActionButtonProps={{
        loading: attaching,
        onClick: handleAttach,
        disabled: selectedContainers.length === 0,
        saveText: selectedContainers.length > 1 ? `Attach ${selectedContainers.length}` : "Attach",
      }}
    >
      <Stack spacing={2}>
        {/* A caption, not an alert: this text is always here and says how the
            picker works. An alert reads as something having gone wrong, which
            is the register conditional messages need to keep for themselves. */}
        <Typography variant="caption" color="text.secondary">
          Attached containers clock to the event&apos;s 60-day removal deadline. Only containers
          that can join are listed: active containers, in a central or satellite area, whose waste —
          every fill event — falls within the event&apos;s window (start date through the 60-day
          deadline). Waste added outside the window can&apos;t co-mingle under the event. Empty
          containers can always be attached.
        </Typography>
        {reliefAdvisory && (
          <Typography variant="caption" color="text.secondary">
            {reliefAdvisory.note}
          </Typography>
        )}
        {optionsError && (
          <Typography variant="caption" color="error">
            The containers could not be loaded. Close the dialog and try again.
          </Typography>
        )}
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={containerOptions}
          loading={loadingOptions}
          noOptionsText={
            noneEligible
              ? "No containers can join: only active containers whose waste all falls inside the event's window are eligible."
              : "No options"
          }
          getOptionLabel={containerOptionLabel}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedContainers}
          onChange={(_event, value) => setSelectedContainers([...value])}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {containerOptionLabel(option)}
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} label="Containers" autoFocus placeholder="Search containers" />
          )}
        />
      </Stack>
    </Dialog>
  );
}
